import Decimal from 'decimal.js'
import { useEffect, useState } from 'react'
import { parseDollarAmount } from '../../../lib/util/parse'
import { present } from '../../../lib/util/present'
import { tryParseDecimal } from '../../../lib/util/tryParseDecimal'
import { Tooltip } from '../tooltip'
import { DisclosureSection } from './disclosureSection'

/**
 * The money fields shared between the new- and edit-expense forms, presented with
 * progressive disclosure. The member always sees a single "Amount" (what they paid);
 * the pre-discount ("listed") amount and the still-owed split are revealed only when
 * they indicate a discount or an unpaid balance.
 *
 * Internally the four canonical amount fields are kept in sync so the stored expense
 * shape is unchanged. The identity is:
 *   listedAmount = discountAmount + paidAmount + remainingOwedAmount
 * where `paidAmount` is what the member has actually paid so far and the post-discount
 * responsibility is `paidAmount + remainingOwedAmount`.
 */
export type ExpenseAmountFieldsModel = {
  listedAmount?: string | null
  discountAmount?: string | null
  paidAmount?: string | null
  remainingOwedAmount?: string | null
}

/** The recomputed amounts pushed back to the parent. Always plain strings (never null). */
export type ExpenseAmountFieldsPatch = {
  listedAmount?: string
  discountAmount?: string
  paidAmount?: string
  remainingOwedAmount?: string
}

export type ExpenseAmountFieldsErrors = {
  [K in keyof ExpenseAmountFieldsModel]?: string
}

export interface ExpenseAmountFieldsProps {
  value: ExpenseAmountFieldsModel
  onChange: (patch: ExpenseAmountFieldsPatch) => void
  validationErrors?: ExpenseAmountFieldsErrors
  clearValidationError?: (field: keyof ExpenseAmountFieldsModel) => void
  /** Field names to disable. Accepts the wider form-model key unions from either expense form. */
  disabledFields?: readonly string[]
}

function fmt(s?: string | null): string {
  const d = tryParseDecimal(s)
  return d ? d.toFixed(2).replace(/\.00$/, '') : s ?? ''
}

/**
 * Re-derives the four canonical amount fields from the currently-typed values and
 * which disclosures are open. `discountAmount` is always derived; `listedAmount` is
 * derived (= responsibility) when the discount section is closed, and
 * `remainingOwedAmount` is forced to 0 when the payment-details section is closed.
 */
export function computeAmounts(
  value: ExpenseAmountFieldsModel,
  typed: Partial<ExpenseAmountFieldsModel>,
  showDiscount: boolean,
  showPaymentDetails: boolean,
): ExpenseAmountFieldsPatch {
  const paidRaw = typed.paidAmount !== undefined ? typed.paidAmount : value.paidAmount ?? ''
  const owedRawInput =
    typed.remainingOwedAmount !== undefined ? typed.remainingOwedAmount : value.remainingOwedAmount ?? ''
  const listedRawInput = typed.listedAmount !== undefined ? typed.listedAmount : value.listedAmount ?? ''

  const owedRaw = showPaymentDetails ? owedRawInput : '0'
  const paidD = tryParseDecimal(paidRaw) ?? new Decimal(0)
  const owedD = tryParseDecimal(owedRaw) ?? new Decimal(0)
  const responsibility = paidD.add(owedD)

  // Nothing has actually been typed yet (e.g. the member merely focused and blurred an
  // empty Amount field). Leave the derived fields blank rather than committing "0.00" —
  // otherwise an untouched Amount field would silently satisfy the required-field check.
  const nothingEntered = paidRaw === '' && (!showPaymentDetails || owedRawInput === '')

  let listed: string
  let discount: string
  if (showDiscount) {
    listed = listedRawInput ?? ''
    const listedD = tryParseDecimal(listed)
    discount = listedD ? listedD.sub(responsibility).toFixed(2) : '0'
  } else {
    listed = nothingEntered ? '' : responsibility.toFixed(2)
    discount = '0'
  }

  return {
    // paidRaw/owedRaw are typed `string | null` (the model allows null, e.g. from a nullable
    // ExpenseModel column), even though in practice they're always assigned string literals.
    paidAmount: paidRaw ?? '',
    remainingOwedAmount: showPaymentDetails ? owedRaw ?? '' : '0',
    listedAmount: listed,
    discountAmount: discount,
  }
}

/**
 * Validates the four canonical amount fields, shared between the new- and edit-expense
 * forms' validateRow functions so the rules only need to change in one place.
 */
export function validateAmountFields(row: ExpenseAmountFieldsModel): ExpenseAmountFieldsErrors {
  const errors: ExpenseAmountFieldsErrors = {}

  const listedAmount = tryParseDecimal(row.listedAmount)
  const discountAmount = tryParseDecimal(row.discountAmount)
  const paidAmount = tryParseDecimal(row.paidAmount)
  const remainingOwedAmount = tryParseDecimal(row.remainingOwedAmount)

  if (!listedAmount) {
    errors.listedAmount = 'Please enter a valid dollar amount'
  } else if (listedAmount.lessThan(0)) {
    errors.listedAmount = 'Listed amount cannot be less than 0'
  }

  if (!discountAmount) {
    // Only reached if row was loaded from stale persisted state before the ExpenseAmountFields
    // backfill effect runs — computeAmounts always produces a valid string in normal flow, so
    // this isn't a user-facing validation path.
    errors.discountAmount = 'Please enter a valid dollar amount'
  } else if (discountAmount.lessThan(0)) {
    // A negative discount means the pre-discount amount was entered lower than what was paid —
    // flag the field the member actually sees and can fix (the "Amount Before Discount" input),
    // not the derived discount value.
    errors.listedAmount = 'Amount before discount must be greater than or equal to the amount you paid'
  }

  if (!paidAmount) {
    errors.paidAmount = 'Please enter a valid dollar amount'
  } else if (paidAmount.lessThan(0)) {
    errors.paidAmount = 'Paid amount cannot be less than 0'
  }

  if (!remainingOwedAmount) {
    errors.remainingOwedAmount = 'Please enter a valid dollar amount'
  } else if (remainingOwedAmount.lessThan(0)) {
    errors.remainingOwedAmount = 'Owed amount cannot be less than 0'
  }

  if (listedAmount && discountAmount && paidAmount && remainingOwedAmount) {
    if (!listedAmount.sub(discountAmount).sub(paidAmount).eq(remainingOwedAmount)) {
      errors.remainingOwedAmount = 'The amounts do not total up correctly'
    }
  }

  return errors
}

export function ExpenseAmountFields({
  value,
  onChange,
  validationErrors,
  clearValidationError,
  disabledFields,
}: ExpenseAmountFieldsProps) {
  // Open a disclosure automatically when the loaded/prefilled expense already has a
  // discount or an outstanding balance (e.g. editing an existing expense or a scanned
  // receipt). The override lets the member expand/collapse it manually thereafter.
  const hasDiscount = tryParseDecimal(value.discountAmount)?.greaterThan(0) ?? false
  const hasOwed = tryParseDecimal(value.remainingOwedAmount)?.greaterThan(0) ?? false

  const [showDiscountOverride, setShowDiscountOverride] = useState<boolean | undefined>(undefined)
  const [showOwedOverride, setShowOwedOverride] = useState<boolean | undefined>(undefined)
  // validationErrors.discountAmount/remainingOwedAmount's "required" messages can only fire from
  // stale/missing persisted data (computeAmounts always forces both to a valid '0' when their
  // section is closed) — but if that ever happens, force the section open so the error is never
  // stuck invisible behind a closed disclosure, same principle as useMoreOptionsDisclosure's
  // hasUnresolvedShareability override.
  //
  // Deliberately NOT watching validationErrors.listedAmount here too: a blank, untouched Amount
  // field also produces a "please enter a valid dollar amount" listedAmount error (via
  // computeAmounts' nothingEntered path) while this section is closed — but that case already
  // has a visible error on the always-shown Amount field, so popping the discount section open
  // for it would be a false alarm on every blank-form submit attempt. listedAmount errors caused
  // by an actual negative discount only ever occur while this section is already open (closing
  // it always resets discountAmount to a valid '0' first), so they never need this rescue.
  const showDiscount = !!validationErrors?.discountAmount || (showDiscountOverride ?? hasDiscount)
  const showPaymentDetails = !!validationErrors?.remainingOwedAmount || (showOwedOverride ?? hasOwed)

  const push = (
    typed: Partial<ExpenseAmountFieldsModel>,
    flags?: { showDiscount?: boolean; showPaymentDetails?: boolean },
  ) => {
    const sd = flags?.showDiscount ?? showDiscount
    const sp = flags?.showPaymentDetails ?? showPaymentDetails
    onChange(computeAmounts(value, typed, sd, sp))
  }

  // Backfill any derived amount the caller hasn't supplied (e.g. a Textract/OCR prefill that
  // sets paidAmount/listedAmount/discountAmount but never remainingOwedAmount, or
  // expenseModelToFormModel's discountAmount short-circuiting to null/'' when listedAmount or
  // paidAmount is missing). Without this, a member who never touches an amount input before
  // submitting would fail validation on a field hidden inside a still-closed disclosure
  // section, with no visible error to act on. Uses `!present(...)` rather than `=== undefined`
  // so null and '' are backfilled too, not just undefined.
  // `showDiscount`/`showPaymentDetails` and `onChange` are intentionally left out of the deps
  // array below (see eslint-disable): `showDiscount`/`showPaymentDetails` are themselves
  // derived from value.discountAmount/value.remainingOwedAmount (the very fields gating this
  // effect), so whenever the guard is true — before any user interaction sets an override —
  // both are deterministically false in that same render; `onChange` is a fresh, unmemoized
  // function every render and isn't something this one-shot backfill needs to react to.
  useEffect(() => {
    if (
      !present(value.listedAmount) ||
      !present(value.discountAmount) ||
      !present(value.remainingOwedAmount)
    ) {
      onChange(computeAmounts(value, {}, showDiscount, showPaymentDetails))
    }
    // onChange/showDiscount/showPaymentDetails intentionally excluded (see comment above): adding
    // `onChange` — a new function reference every render — would make this effect re-run on
    // every keystroke instead of only when the guarded fields are actually missing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.paidAmount, value.listedAmount, value.discountAmount, value.remainingOwedAmount])

  const handleChange = (field: keyof ExpenseAmountFieldsModel, rawValue: string) => {
    const parsed = (rawValue && parseDollarAmount(rawValue)) || ''
    clearValidationError?.(field)
    push({ [field]: parsed })
  }

  // Reformat the visible inputs to trimmed dollar amounts and re-derive the rest.
  const handleBlur = () => {
    const reformatted: Partial<ExpenseAmountFieldsModel> = {
      paidAmount: fmt(value.paidAmount),
    }
    if (showDiscount) {
      reformatted.listedAmount = fmt(value.listedAmount)
    }
    if (showPaymentDetails) {
      reformatted.remainingOwedAmount = fmt(value.remainingOwedAmount)
    }
    onChange(computeAmounts(value, reformatted, showDiscount, showPaymentDetails))
  }

  // Use the `next` value DisclosureSection's onToggle hands back rather than recomputing
  // `!showDiscount`/`!showPaymentDetails` locally, so this doesn't silently assume the two
  // always stay in lockstep.
  const toggleDiscount = (next: boolean) => {
    setShowDiscountOverride(next)
    clearValidationError?.('discountAmount')
    clearValidationError?.('listedAmount')
    push({}, { showDiscount: next })
  }

  const togglePaymentDetails = (next: boolean) => {
    setShowOwedOverride(next)
    clearValidationError?.('remainingOwedAmount')
    push({}, { showPaymentDetails: next })
  }

  const discountDisplay = fmt(value.discountAmount)

  return (
    <>
      <div className="input-group has-validation">
        <label className="input-group-text">
          <span className="d-none d-md-inline">Amount</span>
          <Tooltip
            className="d-none d-md-inline ms-auto"
            tooltip="The amount you paid for this expense. You can add a discount or an unpaid balance below." />
          <Tooltip
            className="d-inline d-md-none"
            tooltip="The amount you paid for this expense. You can add a discount or an unpaid balance below.">
            Amount
          </Tooltip>
        </label>
        <input
          name="paidAmount"
          type="text"
          className={`form-control ${validationErrors?.paidAmount ? 'is-invalid' : ''}`}
          value={`$${value.paidAmount || ''}`}
          pattern="\$\d+(\.\d{0,2})?"
          disabled={disabledFields?.includes('paidAmount')}
          onBlur={handleBlur}
          onChange={(e) => handleChange('paidAmount', e.target.value)} />
        {validationErrors?.paidAmount && (
          <div className="invalid-feedback show">{validationErrors.paidAmount}</div>
        )}
      </div>

      <DisclosureSection
        label="I received a discount"
        open={showDiscount}
        onToggle={toggleDiscount}>
        <div className="input-group has-validation">
          <label className="input-group-text">
            <span className="d-none d-md-inline">Amount Before Discount</span>
            <Tooltip
              className="d-none d-md-inline ms-auto"
              tooltip="The listed amount before any discounts, such as a self-pay (cash) discount or financial aid." />
            <Tooltip
              className="d-inline d-md-none"
              tooltip="The listed amount before any discounts, such as a self-pay (cash) discount or financial aid.">
              Pre-Discount
            </Tooltip>
          </label>
          <input
            name="listedAmount"
            type="text"
            className={`form-control ${validationErrors?.listedAmount || validationErrors?.discountAmount ? 'is-invalid' : ''}`}
            value={`$${value.listedAmount || ''}`}
            pattern="\$\d+(\.\d{0,2})?"
            disabled={disabledFields?.includes('listedAmount')}
            onBlur={handleBlur}
            onChange={(e) => handleChange('listedAmount', e.target.value)} />
          {(validationErrors?.listedAmount || validationErrors?.discountAmount) && (
            <div className="invalid-feedback show">
              {validationErrors?.listedAmount || validationErrors?.discountAmount}
            </div>
          )}
        </div>
        {hasDiscount && (
          <p className="text-success small mb-0 mt-1">You saved ${discountDisplay} with this discount.</p>
        )}
      </DisclosureSection>

      <DisclosureSection
        label="I haven't fully paid this yet"
        open={showPaymentDetails}
        onToggle={togglePaymentDetails}>
        <div className="input-group has-validation">
          <label className="input-group-text">
            <span className="d-none d-md-inline">Amount You Still Owe</span>
            <Tooltip
              className="d-none d-md-inline ms-auto"
              tooltip="The amount you still owe after any discount and the payment above. Leave this closed if you have paid in full." />
            <Tooltip
              className="d-inline d-md-none"
              tooltip="The amount you still owe after any discount and the payment above. Leave this closed if you have paid in full.">
              Owed
            </Tooltip>
          </label>
          <input
            name="remainingOwedAmount"
            type="text"
            className={`form-control ${validationErrors?.remainingOwedAmount ? 'is-invalid' : ''}`}
            value={`$${value.remainingOwedAmount || ''}`}
            pattern="\$\d+(\.\d{0,2})?"
            disabled={disabledFields?.includes('remainingOwedAmount')}
            onBlur={handleBlur}
            onChange={(e) => handleChange('remainingOwedAmount', e.target.value)} />
          {validationErrors?.remainingOwedAmount && (
            <div className="invalid-feedback show">{validationErrors.remainingOwedAmount}</div>
          )}
        </div>
      </DisclosureSection>
    </>
  )
}
